import React from 'react';

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { Card, Descriptions, Empty, Flex, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData } from 'react-router-dom';

import { formatDate, formatDateTime } from '../../../utils/dateTime';

// Admin's read-only ReturnReceipt detail. Shape matches the controller's
// return_receipt_detail_payload — see app/controllers/api/admin/v1/
// return_receipts_controller.rb. We do NOT reuse the operator detail type
// because admin payload omits permission flags (verifiable / completable /
// cancelable / reopenable) and exposes a slightly different ref shape
// (e.g. `inbound: { id, name }` vs operator's flat `inbound_id` / `inbound_name`).
interface ProductRef {
  id: string | null;
  sku: string | null;
  name: string | null;
  barcode: string | null;
}

interface InventoryTypeRef {
  id: string | null;
  name: string | null;
  code: string | null;
}

interface ReceiptItem {
  id: string;
  product_id: string | null;
  product: ProductRef | null;
  inventory_type_id: string | null;
  inventory_type: InventoryTypeRef | null;
  return_order_item_id: string | null;
  batch: string | null;
  expiration_date: string | null;
  manufacturing_date: string | null;
  quantity: number | null;
  received_quantity: number | null;
  custom_attributes: unknown;
  serial_numbers: string[];
  note: string | null;
}

interface ReceiptVideo {
  id: string;
  public_url: string | null;
  created_at: string | null;
}

interface ReceiptRefMerchant {
  id: string;
  name: string | null;
}

interface ReceiptRefReturnOrder {
  id: string;
  name: string | null;
  status: string;
}

interface ReceiptRefInbound {
  id: string;
  name: string | null;
}

interface ReceiptRefVerifier {
  id: string;
  name: string | null;
}

interface ReturnReceiptDetail {
  id: string;
  name: string | null;
  status: string;
  merchant: ReceiptRefMerchant | null;
  warehouse: ReceiptRefMerchant | null;
  return_order: ReceiptRefReturnOrder | null;
  verifier: ReceiptRefVerifier | null;
  verifier_name: string | null;
  verified_at: string | null;
  done_at: string | null;
  inbound: ReceiptRefInbound | null;
  tracking_number: string | null;
  box_count: number | null;
  shipping_fee: number | string | null;
  note: string | null;
  items: ReceiptItem[];
  videos: ReceiptVideo[];
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  pending: { color: 'default', icon: <SyncOutlined /> },
  in_progress: { color: 'processing', icon: <ClockCircleOutlined /> },
  verified: { color: 'processing', icon: <CheckCircleOutlined /> },
  done: { color: 'success', icon: <CheckCircleOutlined /> },
  canceled: { color: 'error', icon: <CloseCircleOutlined /> },
};

// custom_attributes 來自後端的 jsonb，admin 端只負責「可讀地」呈現給內部人員看。
// 直接 stringify 維持原本 key 順序與型別資訊，比花式 key-value table 更好除錯。
function renderCustomAttributes(value: unknown): string {
  if (value == null) return '-';
  if (typeof value === 'object' && Object.keys(value as Record<string, unknown>).length === 0) {
    return '-';
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '-';
  }
}

export default function ReturnReceiptShow() {
  const receipt = useLoaderData() as ReturnReceiptDetail | null;
  const { t } = useTranslation();

  if (!receipt) {
    return (
      <div>
        <Empty description={t('common.error.no_data', { defaultValue: '無資料' })} />
      </div>
    );
  }

  const getStatusTag = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {t(`enums.return_receipt.status.${status}`, { defaultValue: status })}
      </Tag>
    );
  };

  const itemColumns: ColumnsType<ReceiptItem> = [
    {
      title: t('models.return_order_item.product', { defaultValue: '商品' }),
      key: 'product',
      render: (_, record) => {
        const name = record.product?.name || '-';
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.product_id ? (
                <Link to={`/products/${record.product_id}`}>{name}</Link>
              ) : (
                name
              )}
            </div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.product?.sku || ''}</div>
          </div>
        );
      },
    },
    {
      title: t('models.return_order_item.inventory_type', { defaultValue: '庫存類型' }),
      key: 'inventory_type',
      render: (_, record) => record.inventory_type?.name || '-',
    },
    {
      title: t('models.return_order_item.batch', { defaultValue: '批號' }),
      dataIndex: 'batch',
      key: 'batch',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.return_order_item.expiration_date', { defaultValue: '效期' }),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('admin.return_receipt.field.manufacturing_date', { defaultValue: '製造日' }),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('models.return_order_item.quantity', { defaultValue: '應收數量' }),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (quantity: number | null, record) => {
        const verified = record.received_quantity ?? 0;
        const total = quantity ?? 0;
        const hasDiscrepancy = verified !== total;
        return (
          <div style={{ textAlign: 'right' }}>
            <div>
              <span
                style={{
                  color: hasDiscrepancy ? '#ff4d4f' : '#000',
                  fontWeight: hasDiscrepancy ? 500 : 400,
                }}
              >
                {verified}
              </span>
              <span style={{ color: '#999', margin: '0 4px' }}>/</span>
              <span style={{ color: '#666' }}>{total}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: t('admin.return_receipt.field.serial_numbers', { defaultValue: '序號' }),
      key: 'serial_numbers',
      render: (_, record) =>
        record.serial_numbers && record.serial_numbers.length > 0
          ? record.serial_numbers.join(', ')
          : '-',
    },
    {
      title: t('admin.return_receipt.field.custom_attributes', { defaultValue: '自訂屬性' }),
      key: 'custom_attributes',
      render: (_, record) => (
        <Typography.Text code style={{ fontSize: 12 }}>
          {renderCustomAttributes(record.custom_attributes)}
        </Typography.Text>
      ),
    },
    {
      title: t('models.return_order_item.note', { defaultValue: '備註' }),
      dataIndex: 'note',
      key: 'note',
      render: (v: string | null) => v || '-',
    },
  ];

  const breadcrumbItems: Array<string | { label: string; path?: string }> = [
    { label: t('admin.menu.returns.title', { defaultValue: '退貨' }) },
    { label: t('admin.menu.returns.list', { defaultValue: '退貨列表' }), path: '/return_orders' },
    ...(receipt.return_order
      ? [
          {
            label: receipt.return_order.name || receipt.return_order.id,
            path: `/return_orders/${receipt.return_order.id}`,
          },
        ]
      : []),
    { label: receipt.name || receipt.id },
  ];

  return (
    <div>
      <ListPageHeader basePath="/admin" items={breadcrumbItems} />

      <Card
        title={t('admin.return_receipt.section.basic', { defaultValue: '基本資訊' })}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('models.return_receipt.name', { defaultValue: '驗收單號' })}>
            {receipt.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('models.return_order.status', { defaultValue: '狀態' })}
          >
            {getStatusTag(receipt.status)}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('models.return_order.merchant', { defaultValue: '貨主' })}
          >
            {receipt.merchant?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('admin.return_receipt.field.warehouse', { defaultValue: '倉庫' })}
          >
            {receipt.warehouse?.name || '-'}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('models.return_order.name', { defaultValue: '退貨單' })}
          >
            {receipt.return_order ? (
              <Flex align="center" gap={8}>
                <Link to={`/return_orders/${receipt.return_order.id}`}>
                  {receipt.return_order.name || receipt.return_order.id}
                </Link>
                <Tag>
                  {t(`enums.return_order.status.${receipt.return_order.status}`, {
                    defaultValue: receipt.return_order.status,
                  })}
                </Tag>
              </Flex>
            ) : (
              '-'
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('models.return_receipt.tracking_number', { defaultValue: '物流追蹤號' })}
          >
            {receipt.tracking_number || '-'}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('admin.return_receipt.field.box_count', { defaultValue: '箱數' })}
          >
            {receipt.box_count ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('admin.return_receipt.field.shipping_fee', { defaultValue: '運費' })}
          >
            {receipt.shipping_fee == null ? '-' : String(receipt.shipping_fee)}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('admin.return_receipt.field.verifier', { defaultValue: '驗收人' })}
          >
            {receipt.verifier?.name || receipt.verifier_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('admin.return_receipt.field.verified_at', { defaultValue: '驗收時間' })}
          >
            {receipt.verified_at ? formatDateTime(receipt.verified_at) : '-'}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('admin.return_receipt.field.done_at', { defaultValue: '入庫時間' })}
          >
            {receipt.done_at ? formatDateTime(receipt.done_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('admin.return_receipt.field.inbound', { defaultValue: '已入庫' })}
          >
            {receipt.inbound ? (
              <Link to={`/inbounds/${receipt.inbound.id}`}>
                {receipt.inbound.name || receipt.inbound.id}
              </Link>
            ) : (
              '-'
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('models.return_order.note', { defaultValue: '備註' })}
            span={2}
          >
            {receipt.note || '-'}
          </Descriptions.Item>

          <Descriptions.Item
            label={t('common.created_at', { defaultValue: '建立時間' })}
          >
            {receipt.created_at ? formatDateTime(receipt.created_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('common.updated_at', { defaultValue: '更新時間' })}
          >
            {receipt.updated_at ? formatDateTime(receipt.updated_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={t('admin.return_receipt.section.items', { defaultValue: '驗收品項' })}
        style={{ marginBottom: 16 }}
      >
        {receipt.items && receipt.items.length > 0 ? (
          <Table<ReceiptItem>
            columns={itemColumns}
            dataSource={receipt.items}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description={t('common.error.no_data', { defaultValue: '無資料' })} />
        )}
      </Card>

      <Card title={t('admin.return_receipt.section.videos', { defaultValue: '驗收影片' })}>
        {receipt.videos && receipt.videos.length > 0 ? (
          <Flex vertical gap={8}>
            {receipt.videos.map((video) => (
              <Flex key={video.id} align="center" gap={8}>
                <LinkOutlined />
                {video.public_url ? (
                  <a href={video.public_url} target="_blank" rel="noopener noreferrer">
                    {video.id}
                  </a>
                ) : (
                  <Typography.Text type="secondary">{video.id}</Typography.Text>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {video.created_at ? formatDateTime(video.created_at) : ''}
                </Typography.Text>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Empty
            description={t('admin.return_receipt.videos_empty', { defaultValue: '尚未上傳影片' })}
          />
        )}
      </Card>
    </div>
  );
}
